import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import type { InboxAlertItem, NotificationDto, NotificationType } from '@cosvision/shared';
import { dayjs } from '../../lib/format.js';
import {
  useInboxListQuery,
  useInboxUnreadQuery,
  useMarkAlertReadOptimistic,
  useMarkAllActivityReadMutation,
  useMarkNotificationReadOptimistic,
} from '../../hooks/useInbox.js';

interface InboxBellProps {
  /** CO + officers see this bell. Operators (DATA_ENTRY) have no inbox surface. */
  enabled: boolean;
  /** True for the CO — controls whether the Alerts section renders. */
  alertsEnabled: boolean;
}

/**
 * Single header bell that unifies the two notification streams (task /
 * messaging activity + financial alerts). Replaces the previous
 * `<AlertsBell />` and `<NotificationBell />` mounts so the CO sees one
 * bell, sectioned by type. Mark-read is optimistic per item with rollback.
 */
export function InboxBell({ enabled, alertsEnabled }: InboxBellProps) {
  const [open, setOpen] = useState(false);
  const countQ = useInboxUnreadQuery(enabled);
  const listQ = useInboxListQuery(enabled && open);
  const markNotif = useMarkNotificationReadOptimistic();
  const markAlert = useMarkAlertReadOptimistic();
  const markAll = useMarkAllActivityReadMutation();
  const navigate = useNavigate();

  if (!enabled) return null;
  const total = countQ.data?.total ?? 0;

  function openActivity(n: NotificationDto): void {
    if (!n.isRead) markNotif.mutate(n.id);
    setOpen(false);
    if (n.linkPath) navigate(n.linkPath);
  }

  function openAlert(a: InboxAlertItem): void {
    if (!a.isRead) markAlert.mutate(a.id);
    setOpen(false);
    navigate('/#alerts-panel');
    requestAnimationFrame(() => {
      document.getElementById('alerts-panel')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  const activity = listQ.data?.activity ?? [];
  const alerts = alertsEnabled ? (listQ.data?.alerts ?? []) : [];
  const showAlertsSection = alertsEnabled && alerts.length > 0;
  const activityUnread = countQ.data?.activity ?? 0;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={total > 0 ? `Inbox — ${String(total)} unread` : 'Inbox'}
        aria-expanded={open ? 'true' : 'false'}
        className="relative grid h-9 w-9 place-items-center rounded-full text-slate-500 hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-bn-300"
      >
        <BellIcon />
        {total > 0 ? (
          <span
            aria-hidden="true"
            className="absolute -top-0.5 -right-0.5 inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-rag-red px-1 text-[10px] font-bold leading-none text-white"
          >
            {total > 99 ? '99+' : total}
          </span>
        ) : null}
      </button>

      {open ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 z-40 mt-2 w-96 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
              <span className="text-sm font-semibold text-slate-800">Inbox</span>
              {activityUnread > 0 ? (
                <button
                  type="button"
                  className="text-xs font-medium text-bn-700 hover:text-bn-600 disabled:opacity-50"
                  onClick={() => markAll.mutate()}
                  disabled={markAll.isPending}
                >
                  Mark activity read
                </button>
              ) : null}
            </div>
            <div className="max-h-[28rem] overflow-y-auto">
              {listQ.isLoading ? (
                <p className="px-4 py-6 text-center text-sm text-slate-400">Loading…</p>
              ) : activity.length === 0 && alerts.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-400">
                  You&rsquo;re all caught up.
                </p>
              ) : (
                <>
                  {/* Activity always renders first. */}
                  <SectionHeader label="Activity" />
                  {activity.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-slate-400">No recent activity.</p>
                  ) : (
                    <ul className="divide-y divide-slate-50">
                      {activity.map((n) => (
                        <ActivityRow key={n.id} item={n} onOpen={openActivity} />
                      ))}
                    </ul>
                  )}
                  {showAlertsSection ? (
                    <>
                      <SectionHeader label="Financial alerts" />
                      <ul className="divide-y divide-slate-50">
                        {alerts.map((a) => (
                          <AlertRow key={a.id} item={a} onOpen={openAlert} />
                        ))}
                      </ul>
                    </>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function SectionHeader({ label }: { label: string }) {
  return (
    <div className="bg-slate-50 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
      {label}
    </div>
  );
}

const TYPE_ICON: Record<NotificationType, string> = {
  TASK_ASSIGNED: '🎯',
  TASK_DUE_SOON: '⏰',
  TASK_STATUS_CHANGED: '🔁',
  TASK_COMMENT: '💬',
  TASK_MENTION: '🔔',
  MESSAGE_MENTION: '💌',
  TASK_UPDATE_REQUESTED: '📋',
  TASK_UPDATE_POSTED: '✅',
};

const TYPE_TINT: Record<NotificationType, string> = {
  TASK_ASSIGNED: 'bg-bn-100 text-bn-700',
  TASK_DUE_SOON: 'bg-amber-100 text-amber-700',
  TASK_STATUS_CHANGED: 'bg-slate-100 text-slate-600',
  TASK_COMMENT: 'bg-slate-100 text-slate-600',
  TASK_MENTION: 'bg-rose-100 text-rose-700',
  MESSAGE_MENTION: 'bg-rose-100 text-rose-700',
  TASK_UPDATE_REQUESTED: 'bg-bn-100 text-bn-700',
  TASK_UPDATE_POSTED: 'bg-emerald-100 text-emerald-700',
};

export function ActivityRow({
  item,
  onOpen,
}: {
  item: NotificationDto;
  onOpen: (n: NotificationDto) => void;
}) {
  const icon = TYPE_ICON[item.type] ?? '•';
  const tint = TYPE_TINT[item.type] ?? 'bg-slate-100 text-slate-600';
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="flex w-full items-start gap-2.5 px-4 py-3 text-left hover:bg-slate-50"
      >
        <span className={clsx('grid h-7 w-7 place-items-center rounded-full text-sm', tint)}>
          {icon}
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-slate-700">{item.message}</span>
          <span className="mt-0.5 block text-[11px] text-slate-400">
            {dayjs(item.createdAt).fromNow()}
          </span>
        </span>
        {!item.isRead ? (
          <span
            aria-hidden="true"
            className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-bn-600"
          />
        ) : null}
      </button>
    </li>
  );
}

const ALERT_SEVERITY_TINT: Record<InboxAlertItem['severity'], string> = {
  INFO: 'bg-slate-100 text-slate-600',
  WARNING: 'bg-amber-100 text-amber-700',
  CRITICAL: 'bg-rose-100 text-rose-700',
};

export function AlertRow({
  item,
  onOpen,
}: {
  item: InboxAlertItem;
  onOpen: (a: InboxAlertItem) => void;
}) {
  const tint = ALERT_SEVERITY_TINT[item.severity];
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="flex w-full items-start gap-2.5 px-4 py-3 text-left hover:bg-slate-50"
      >
        <span className={clsx('grid h-7 w-7 place-items-center rounded-full text-sm', tint)}>
          ৳
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm text-slate-700">{item.message}</span>
          <span className="mt-0.5 block text-[11px] text-slate-400">
            {item.fundCode ? `${item.fundCode} · ` : ''}
            {dayjs(item.triggeredAt).fromNow()}
          </span>
        </span>
        {!item.isRead ? (
          <span
            aria-hidden="true"
            className="mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full bg-bn-600"
          />
        ) : null}
      </button>
    </li>
  );
}

export function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
